import React from 'react';
import { History, Check, Eye, X, RefreshCw, Clock, FileText, Stethoscope, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * RestoreSessionDialog - "Pick Up Where You Left Off"
 * Shows when previous session data is found on mount.
 * Rich context: timestamp, chief complaint, visit type, notes preview, generated status.
 */
const RestoreSessionDialog = ({
  open,
  onOpenChange,
  pendingRestoreData,
  onRestore,
  onDone,
  onHistory,
  onDiscard
}) => {
  const handleChoice = (choice) => {
    switch (choice) {
      case 'restore': onRestore(); break;
      case 'done': onDone(); break;
      case 'history': onHistory(); break;
      case 'discard':
      default: onDiscard(); break;
    }
  };

  // Build context from saved data
  const data = pendingRestoreData || {};
  const savedAt = data.savedAt ? new Date(data.savedAt) : null;
  const timeAgo = savedAt ? getTimeAgo(savedAt) : null;
  const hasResult = !!data.result;
  const noteLength = data.notes?.trim().length || 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleChoice('discard');
    }}>
      <DialogContent className="sm:max-w-md bg-card border-border" data-testid="restore-session-dialog">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-foreground flex items-center gap-2 text-lg">
            <div className="p-2 rounded-lg bg-primary/10">
              <History className="w-5 h-5 text-primary" />
            </div>
            Pick Up Where You Left Off
          </DialogTitle>
          <DialogDescription className="text-muted-foreground sr-only">
            Resume your previous session or start fresh
          </DialogDescription>
        </DialogHeader>
        
        {/* Session Card */}
        <div className="rounded-lg border border-border bg-secondary/20 overflow-hidden" data-testid="restore-session-card">
          {/* Time badge */}
          {timeAgo && (
            <div className="flex items-center gap-2 px-3 py-2 bg-secondary/40 border-b border-border/50">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{timeAgo}</span>
              {hasResult && (
                <Badge className="ml-auto text-[10px] h-5 bg-green-500/15 text-green-500 border-green-500/30">
                  <Check className="w-3 h-3 mr-0.5" /> Docs Generated
                </Badge>
              )}
              {!hasResult && noteLength > 50 && (
                <Badge className="ml-auto text-[10px] h-5 bg-amber-500/15 text-amber-500 border-amber-500/30">
                  <AlertCircle className="w-3 h-3 mr-0.5" /> In Progress
                </Badge>
              )}
            </div>
          )}
          
          <div className="p-3 space-y-2">
            {/* Chief complaint & visit context */}
            {data.chiefComplaint && (
              <div className="flex items-start gap-2">
                <Stethoscope className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm font-medium text-foreground line-clamp-1">{data.chiefComplaint}</p>
              </div>
            )}
            
            {/* Notes preview */}
            {data.notes && (
              <div className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                  {data.notes.substring(0, 200)}{data.notes.length > 200 ? '...' : ''}
                </p>
              </div>
            )}

            {/* Quick stats row */}
            <div className="flex items-center gap-3 pt-1 text-[11px] text-muted-foreground/70">
              {data.encounterType && <span>{data.encounterType === 'established' ? 'Established Pt' : 'New Pt'}</span>}
              {data.patientAge && <span>{data.patientAge}yo</span>}
              {data.patientGender && data.patientGender !== '--' && <span>{data.patientGender}</span>}
              {data.manualIcd10 && <span>ICD-10: {data.manualIcd10.split(',').filter(Boolean).length}</span>}
              {noteLength > 0 && <span>{noteLength} chars</span>}
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex flex-col gap-2 sm:flex-col pt-1">
          <Button 
            onClick={() => handleChoice('restore')}
            className="w-full bg-primary hover:bg-primary/90 font-semibold"
            data-testid="restore-continue-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Continue Where I Left Off
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button 
              variant="outline"
              onClick={() => handleChoice('done')}
              className="border-green-500/50 text-green-500 hover:bg-green-500/10"
              data-testid="restore-done-btn"
            >
              <Check className="w-4 h-4 mr-1" />
              Done - Clear
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleChoice('history')}
              className="border-primary/50 text-primary hover:bg-primary/10"
              data-testid="restore-history-btn"
            >
              <Eye className="w-4 h-4 mr-1" />
              See History
            </Button>
          </div>
          <button 
            onClick={() => handleChoice('discard')}
            className="w-full text-xs text-muted-foreground/50 hover:text-muted-foreground py-1 transition-colors"
            data-testid="restore-discard-btn"
          >
            Start Fresh
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function getTimeAgo(date) {
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default RestoreSessionDialog;
